import React from 'react';
import './CSS/TermsOfService.css';

const TermsOfService: React.FC = () => {
    return (
        <div className="terms-container">
            <h1 className="service-title">Terms of Service</h1>
            <p className="intro">
                BEFORE YOU USE THE SERVICES PLEASE READ THESE TERMS AND CONDITIONS (<strong>“TERMS”</strong>) CAREFULLY.
            </p>
            <p className="intro">
                BY SIGNING AN ORDER FORM WITH VIZE LABS INC. (<strong>“VIZE LABS”</strong>) OR USING THE SERVICES, YOU
                AND ANY ENTITIES THAT YOU REPRESENT (<strong>“YOU”</strong> OR <strong>“CUSTOMER”</strong>) AGREE TO BE
                BOUND BY THESE TERMS IN ADDITION TO THE TERMS ON YOUR ORDER FORM. IF CUSTOMER ENTERS INTO A NEW ORDER
                FORM OR RENEWS A SUBSCRIPTION, THE TERMS AND CONDITIONS IN EFFECT ON THE EFFECTIVE DATE WILL APPLY TO
                THAT NEW ORDER FORM AND/OR RENEWED SUBSCRIPTION TERM.
            </p>
            <p className="intro">
                When Vize Labs introduces new or additional features, functionality or services, Vize Labs may provide
                terms or make updates to these Terms that apply to Customer’s use of those new or additional features,
                functionality, or services.
            </p>

            <section>
                <h2 className="section-title">1 DEFINITIONS</h2>
                <p className="definition">
                    The following capitalized terms will have the following meanings whenever used in this Agreement.
                </p>
                <p className="service-text">
                    <strong>1.1</strong> <b>"Agreement"</b> means:
                    <ol type="a">
                        <li>the Order Form that incorporates these Terms by reference;</li>
                        <li>these Terms; and</li>
                        <li>all Statements of Work, as it or they may from time be amended or supplemented.</li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>1.2</strong> <b>"Applicable Laws"</b> means, in respect of any person, property, transaction
                    or
                    event, all applicable Canadian or foreign federal, provincial, state, municipal or local government
                    laws, statutes, rules, by-laws and regulations, and all applicable official rules, policies,
                    notices, directives, orders, judgments and decrees of any Governmental Authority, all as amended
                    from time to time.
                </p>
                <p className="service-text">
                    <strong>1.3</strong> <b>"Authorized User"</b> means an individual who is authorized by Customer to use
                    the
                    Services, and to whom Customer (or, when applicable, Vize Labs at Customer’s request) has supplied a
                    user identification and password (for Services utilizing authentication). Authorized
                    Users may include, for example, employees, consultants, contractors and agents of Customer, and
                    third parties with which Customer transacts business.
                </p>
                <p className="service-text">
                    <strong>1.4</strong> <b>"Business Day"</b> means any day of the year, other than a Saturday, Sunday or
                    any
                    statutory holiday, in the Province of Saskatchewan.
                </p>
                <p className="service-text">
                    <strong>1.5</strong> <b>"Vize Labs Privacy Policy"</b> means the privacy policy of Vize Labs, as it
                    may
                    from time to time be amended or supplemented by Vize Labs. A copy of the current Vize Labs Privacy
                    Policy is available on the Vize Labs website.
                </p>
                <p className="service-text">
                    <strong>1.6</strong> <b>"Customer"</b> means the customer identified in the Order Form that refers to
                    these
                    Terms.
                </p>
                <p className="service-text">
                    <strong>1.7</strong> <b>"Customer Content"</b> means text, audio, video, images, and other information
                    submitted by or for Customer to the Services, and used for the purpose of personalizing the
                    presentation of screens, messaging or other communications from Customer to users.
                </p>
                <p className="service-text">
                    <strong>1.8</strong> <b>"Customer Data"</b> means data submitted by or for Customer to the Services or
                    provided by Vize Labs to Customer in output files generated by the Services, excluding Customer
                    Content, Third Party Content, Third Party Materials, and Service Attributes.
                </p>
                <p className="service-text">
                    <strong>1.9</strong> <b>"Customer Regulator"</b> means any Governmental Authority that has regulatory
                    authority over Customer, and includes privacy regulators.
                </p>
                <p className="service-text">
                    <strong>1.10</strong> <b>"Customer Regulatory Requirements"</b> has the meaning specific in Section
                    3.7.
                </p>
                <p className="service-text">
                    <strong>1.11</strong> <b>"Documentation"</b> means any manuals, instructions or other documents or
                    materials that Vize Labs provides or makes available to Customer in any form or medium and which
                    describe the functionality, components, features or requirements of the Services, including any
                    aspect of
                    the installation, configuration, integration, operation, use, support or maintenance thereof
                </p>
                <p className="service-text">
                    <strong>1.12</strong> <b>"Force Majeure Event"</b> means any event or circumstances beyond the
                    reasonable
                    control of a party, including an act of God, act of government, flood, fire, earthquake, civil
                    unrest, act of terror, strike or other labour problem, Internet or telecommunication service failure
                    or delay, Third-Party Material failure or delay, or a denial of service attack.
                </p>
                <p className="service-text">
                    <strong>1.13</strong> <b>"Governmental Authority"</b> means any governmental or regulatory authority,
                    agency, commission or board of any Canadian or foreign federal, provincial, state, municipal or
                    local government, parliament or legislature, or any court or, without limitation, any other law,
                    regulation or rule-making entity having or purporting to have jurisdiction in the relevant
                    circumstances, and whether now or in the future constituted or existing, or any person acting or
                    purporting to act under the authority of any of them.
                </p>
                <p className="service-text">
                    <strong>1.14</strong> <b>"IP Rights"</b> means any and all registered and unregistered rights granted,
                    applied for or otherwise now or hereafter in existence under or related to any patent, copyright,
                    trademark, trade secret, database protection or other intellectual property rights laws, and all
                    similar or equivalent rights or forms of protection, in any part of the world
                </p>
                <p className="service-text">
                    <strong>1.15</strong> <b>"Licensed Device"</b> means the computer, tablet, smartphone, laptop, or
                    other
                    electronic device on which the Services is intended to be installed and operated.
                </p>
                <p className="service-text">
                    <strong>1.16</strong> <b>"Lighthouse Services"</b> means any additional advisory services which Vize
                    Labs
                    has agreed to provide to Customer, as set out in the Agreement.
                </p>
                <p className="service-text">
                    <strong>1.17</strong> <b>"Losses"</b> means any and all losses, damages, liabilities, deficiencies,
                    claims,
                    actions, judgments, settlements, interest, awards, penalties, fines, costs or expenses of whatever
                    kind.
                </p>
                <p className="service-text">
                    <strong>1.18</strong> <b>"New Version"</b> means any new version of the Services that Vize Labs may
                    from
                    time to time introduce and market generally as a distinct licensed product (as may be indicated by
                    Vize Labs’s designation of a new version number) and which Vize Labs may make available to Customer
                    at an additional cost under a separate written agreement.
                </p>
                <p className="service-text">
                    <strong>1.19</strong> <b>"Non-Vize Labs Application"</b> means a service or software application that
                    is
                    provided by a third party or by Customer and that interoperates with the Services, including,
                    without limitation, Microsoft Office 365, Microsoft Edge (formerly Internet Explorer), Google
                    Chrome, Mozilla Firefox, Apple Safari, Facebook, Workplace by Facebook, Zoom, Twitter and Slack.
                </p>
                <p className="service-text">
                    <strong>1.20</strong> <b>"Open-Source Components"</b> means any software component that is subject to
                    any
                    open-source copyright licence agreement, including any GNU General Public License or GNU Library or
                    Lesser Public License, or other obligation, restriction or licence agreement that substantially
                    conforms to the Open Source Definition as prescribed by the Open Source Initiative or otherwise may
                    require disclosure or licensing to any third party of any source code with which such software
                    component is used or compiled.
                </p>
                <p className="service-text">
                    <strong>1.21</strong> <b>"Order Form"</b> means an ordering document or online order specifying the
                    Services
                    to be provided under the Agreement that is entered into between Customer and Vize Labs, including
                    any schedules, exhibits, addenda, as it may from time to be amended or supplemented.
                </p>
                <p className="service-text">
                    <strong>1.22</strong> <b>"Permitted Use"</b> means use of the Services by an Authorized User for the
                    benefit
                    of Customer or in the ordinary course of Customer’s business.
                </p>
                <p className="service-text">
                    <strong>1.23</strong> <b>"Personal Data"</b> means any information relating to an identified or
                    identifiable
                    natural person; an identifiable natural person is one who can be identified, directly or indirectly,
                    in particular by reference to an identifier such as a name, an identification number, location data,
                    an online identifier or to one or more factors specific to the physical, physiological, genetic,
                    mental, economic, cultural or social identity of that natural person.
                </p>
                <p className="service-text">
                    <strong>1.24</strong> <b>"Regular Support Hours"</b> means 9:00 a.m. to 5:00 p.m. (Saskatchewan Time)
                    during Business Days.
                </p>
                <p className="service-text">
                    <strong>1.25</strong> <b>"Representatives"</b> means, with respect to a party, that party’s employees,
                    officers, directors, consultants, agents, independent contractors, service providers, sublicensees,
                    and subcontractors.
                </p>
                <p className="service-text">
                    <strong>1.26</strong> <b>"Service Attributes"</b> means:
                    <ol type="a">
                        <li> data related to the provision or use of the Services, such as resource identifiers,
                            metadata tags, security and access roles, logs, rules, audit trail events, usage policies,
                            permissions, usage metrics, statistics and analytics;
                            and
                        </li>
                        <li> data required for billing or administration of the Services, including billing and contact
                            information.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>1.27</strong> <b>"Services"</b> means the products and services that are ordered by Customer
                    under
                    the Form and made available online by Vize Labs, as described in the Documentation. Services do not
                    include Non-Vize Labs Applications, Lighthouse Services, or Support Services.
                </p>
                <p className="service-text">
                    <strong>1.28</strong> <b>"Statement of Work"</b> means a statement of work specifying any additional
                    Lighthouse Services not originally specified in the Order Form between Vize Labs and Customer.
                </p>
                <p className="service-text">
                    <strong>1.29</strong> <b>"Support Data"</b> means all data that is provided to Vize Labs by or on
                    behalf of
                    Customer (or that Customer authorizes Vize Labs to obtain from the Services) in relation to a
                    request by Customer for Support Services.
                </p>
                <p className="service-text">
                    <strong>1.30</strong> <b>"Support Services"</b> means any support services described in the Agreement.
                </p>
                <p className="service-text">
                    <strong>1.31</strong> <b>"Territory"</b> means Canada.
                </p>
                <p className="service-text">
                    <strong>1.32</strong> <b>"Third Party Materials"</b> means materials and information, in any form or
                    medium, that are not proprietary to Vize Labs, including any third-party:
                    <ol type="a">
                        <li>documents, data, content or specifications</li>
                        <li>Open-Source Components or other software, hardware or other products, facilities, equipment
                            or devices; and
                        </li>
                        <li>accessories, components, parts or features of any of the foregoing.</li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>1.33</strong> <b>"Upgrade"</b> means any update, upgrade, release or other adaptation or
                    modification of the Services, including any updated Documentation, that Vize Labs may provide to
                    Customer from time to time during the Term, which may contain, among other things, error
                    corrections, enhancements, improvements or other changes to the user interface, functionality,
                    compatibility, capabilities, performance, efficiency or quality of the Services, but does not
                    include any New Version.
                </p>
                <p className="service-text">
                    <strong>1.34</strong> <b>"Usage Data"</b> means Vize Lab’s technical logs, data and learnings about
                    Customer’s use of the Service, but excluding Customer Data.
                </p>
            </section>

            <section>
                <h2 className="section-title">2 PROVISION OF SERVICES BY VIZE LABS</h2>
                <p className="service-text">
                    <strong>2.1</strong> <u>Services.</u> Provided there is compliance by Customer with the Agreement,
                    Vize
                    Labs will:
                    <ol type="a">
                        <li>make the Services available to Customer pursuant to the Agreement;</li>
                        <li>provide Support Services as described in the Agreement in effect from time to time; and</li>
                        <li>
                            use commercially reasonable efforts to make the online services available 24 hours a day, 7
                            days a week, except for
                            <ol type="i">
                                <li>planned downtown (of which Vize Labs will give make best efforts to give advanced
                                    electronic notice of), and
                                </li>
                                <li>any unavailability caused by circumstances beyond Vize Labs’s reasonable control,
                                    including a Force Majeure Event.
                                </li>
                            </ol>
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>2.2</strong> <u>Lighthouse Services.</u> Vize Labs will provide Lighthouse Services in
                    accordance
                    with an Order Form or Statement of Work. Actual meeting times and availability of advisors may vary.
                    Advisor availability is subject to occasional downtime for system and server maintenance, company
                    events, observed Canadian holidays and events beyond Vize Labs’s control.
                </p>
            </section>

            <section>
                <h2 className="section-title">3 USE OF SERVICES BY CUSTOMER</h2>
                <p className="service-text">
                    <strong>3.1</strong> <u>Subscriptions.</u> Unless otherwise provided in the Order Form:
                    <ol type="a">
                        <li>Services are purchased as subscriptions; and</li>
                        <li>Any added subscriptions will terminate on the same date as the underlying subscriptions.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>3.2</strong> <u>Service Limits.</u> Customer’s use of the Services may be subject to service
                    limited specified in the Order Form. Customer will not exceed any such service limits.
                </p>
                <p className="service-text">
                    <strong>3.3</strong> <u>Customer Responsibilities.</u> Customer will:
                    <ol type="a">
                        <li>ensure it possesses all relevant licenses to any Non-Vize Labs Applications which are
                            required for the provision of the Services;
                        </li>
                        <li>be responsible for Authorized User’s compliance with the Agreement and the Documentation;
                        </li>
                        <li>use commercially reasonable efforts to prevent unauthorized access to or use of the Services
                            and Documentation, and notify Vize Labs promptly of any such unauthorized access or use;
                        </li>
                        <li>use Services and Documentation only in accordance with the Agreement, the Documentation, and
                            applicable Laws; and
                        </li>
                        <li>comply with terms of service of any Third Party Materials with which Customer uses
                            Services.
                        </li>
                        <li>
                            <p>
                                notify Vize Labs within 24 hours of any critical security incident or data breach
                                affecting the Customer’s data, detailing the nature, impact, and mitigation steps. The
                                Customer shall also notify Vize Labs at least 30 days before any planned shutdown or
                                immediately upon an unexpected shutdown. The customer should notify Vize Labs of any
                                failures, incidents, concerns, and other complaints.
                            </p>
                            <p>
                                &gt;&gt;&gt; Notifications should be made to:
                                <a href="mailto:support@vizelabs.ca">support@vizelabs.ca</a> Or through the website at:
                                <a href="https://vizelabs.ca/customer-support/"
                                   target="_blank">https://vizelabs.ca/customer-support/</a>
                            </p>
                            <p>
                                &gt;&gt;&gt; Customer support availability: Email support
                                from 8:00 am – 5:00 pm Central Standard Time (Monday – Friday) except for observed
                                holidays within the US & Canada.
                            </p>
                            <p>
                                &gt;&gt;&gt; Emails received out of office hours, will be
                                responded to based on the severity of the issues.
                            </p>
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>3.4</strong> <u>Customer Restrictions.</u> Customer receives no title to or ownership of any
                    copy
                    or of the Services. Without limiting the generality of the foregoing, Customer shall not:
                    <ol type="a">
                        <li>copy, modify, correct, adapt, translate, enhance, create derivative works from, distribute,
                            publicly display, publicly perform, or sublicense the Services;
                        </li>
                        <li>rent, lease, lend, sell, sublicense, assign, distribute, publish, transfer or otherwise make
                            available the Services to any person, including on or in connection with the internet or any
                            time-sharing, service bureau, software as a service, cloud or other technology or service;
                        </li>
                        <li>reverse engineer, decompile, disassemble, decode, or adapt the Services or otherwise attempt
                            to derive or gain access to any of the Services’ source code, in whole or in part;
                        </li>
                        <li>bypass or breach any security device or protection used for or contained in the Services or
                            Documentation;
                        </li>
                        <li>remove, delete, efface, alter, obscure, translate, combine, supplement or otherwise change
                            any trademarks, terms of the Documentation, warranties, disclaimers, or IP Rights,
                            proprietary rights or other symbols, notices, marks or serial numbers on or relating to any
                            copy of the Services or Documentation;
                        </li>
                        <li>use the Services in any manner or for any purpose that infringes, misappropriates or
                            otherwise violates any IP Right, privacy right or other right of any person, or that
                            violates any Applicable Law;
                        </li>
                        <li>use the Services for purposes of: (I) benchmarking or competitive analysis of the Services;
                            (II) developing, using or providing a competing software product or service; or (III) any
                            other purpose that is to Vize Labs’s detriment or commercial disadvantage; or
                        </li>
                        <li>use the Services or Documentation other than for the Permitted Use or in any manner or for
                            any purpose not expressly permitted by this Agreement
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>3.5</strong> <u>Removal of Non-Vize Labs Applications.</u> If Vize Labs receives information
                    that a
                    Non-Vize Labs Application used with the Services by Customer may violate Applicable Laws or
                    third-party rights, Vize Labs may so notify Customer and in such event Customer will promptly
                    disable such Non-Vize Labs Application or modify the Non-Vize Labs Application to resolve the
                    potential violation. If Customer does not take required action in accordance with the above, Vize
                    Labs may disable the applicable Service and/or Non-Vize Labs Application until the potential
                    violation is resolved. If requested by Vize Labs, Customer will confirm such deletion and
                    discontinuance of use in writing and Vize Labs will be authorized to provide a copy of such
                    confirmation to any third party claimant or Governmental Authority, as applicable.
                </p>
                <p className="service-text">
                    <strong>3.6</strong> <u>Non-Vize Labs Providers.</u> Vize Labs or third parties may make available
                    third-party products or services, including Non-Vize Labs Applications and implementation and other
                    consulting services.
                    <ol type="a">
                        <li>Any acquisition by Customer of such products or services, and any exchange of data between
                            Customer and any non-Vize Labs provider, product or service is solely between Customer and
                            the applicable non-Vize Labs provider. Vize Labs does not warrant or support Non-Vize Labs
                            Applications or other non-Vize Labs products or services, whether or not they are designated
                            by Vize Labs as “certified” or otherwise, unless otherwise expressly provided in the Order
                            Form.
                        </li>
                        <li>If Customer chooses to use a Non-Vize Labs Application with the Services, Customer grants
                            Vize Labs permission to allow the Non-Vize Labs Application and its provider to access
                            Customer Data as required for the interoperation of that Non-Vize Labs Application with the
                            Service. Vize Labs is not responsible for any disclosure, modification or deletion of
                            Customer Data resulting from access by such non- Vize Labs Application or its provider.
                        </li>
                        <li>The Services may contain features designed to interoperate with Non-Vize Labs Applications.
                            To use such features, Customer may be required to obtain access to such Non-Vize Labs
                            Applications from their providers, and may be required to grant Vize Labs access to
                            Customer’s accounts on such Non- Vize Labs Applications. Vize Labs cannot guarantee the
                            continued availability of such Service features, and may cease providing them without
                            entitling Customer to any refund, credit, or other compensation, if for example and without
                            limitation, the provider of a Non-Vize Labs Application ceases to make the Non- Vize Labs
                            Application available for interoperation with the corresponding Service features in a manner
                            acceptable to Vize Labs.
                        </li>
                        <li>NOTWITHSTANDING ANY OTHER PART OF THESE TERMS, IN NO CASE IS VIZE LABS LIABLE FOR ANY
                            INACCURATE DATA ORIGINATING OR ARISING FROM NON-VIZE LABS APPLICATIONS.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>3.7</strong> <u>Change to the Services.</u> Vize Labs may make changes to the Services from
                    time to time, in its discretion, including to add, change or remove features or functionality. Vize
                    Labs will provide Customer with not less than 10 days’ prior notice of any major change to the
                    Services that might impact the systems of Customer. For new or additional features, functionality or
                    Services, Vize Labs may require Customer to:
                    <ol type="a">
                        <li> sign a new Order Form or an amended Order Form;</li>
                        <li> agree to pay additional fees; and</li>
                        <li> agree to additional contract terms specific to the feature, functionality or Service.</li>
                    </ol>
                </p>
            </section>

            <section>
                <h2 className="section-title">4 MAINTENANCE AND UPGRADES.</h2>
                <p className="service-text">
                    <strong>4.1</strong> <u>Provision of Maintenance.</u> During the Term, Vize Labs shall exercise
                    commercially reasonable efforts promptly to correct any failure of the Services to perform according
                    to its Specifications (“Maintenance”) during Regular Support Hours.
                </p>
                <p className="service-text">
                    <strong>4.2</strong> <u>Upgrades.</u> During the Term, Vize Labs will provide Customer with all
                    Maintenance releases (including updated Documentation) that Vize Labs may, in its sole discretion,
                    make generally available to its licensees at no additional charge. All Maintenance releases, on
                    being provided by Vize Labs to Customer hereunder, are deemed Services subject to all applicable
                    terms and conditions of this Agreement. Customer will install or download all Maintenance releases
                    as soon as practicable after receipt. Customer does not have any right hereunder to receive any New
                    Versions of the Services that Vize Labs may, in its sole discretion, release from time to time.
                </p>
            </section>

            <section>
                <h2 className="section-title">5 FEES.</h2>
                <p className="service-text">
                    <strong>5.1</strong> <u>Fees and Payment.</u> Customer shall pay to Vize Labs all Subscription Fees
                    and Lighthouse Services Fees (together the “Fees”) set out in the Agreement. Except as otherwise
                    specified in the Agreement
                    <ol type="a">
                        <li>Fees are based on subscriptions purchased and not actual use;</li>
                        <li>payment obligations are non-cancellable and fees paid are non-refundable; and</li>
                        <li>quantities purchased cannot be decreased during relevant subscription term.</li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>5.2</strong> <u>Fee Changes.</u> The Fees set out in the Agreement may be changed from time
                    to time subject to 90 days’ prior written notice from Vize Labs
                </p>
                <p className="service-text">
                    <strong>5.3</strong> <u>Payment Processing Services.</u> Payment processing services for the
                    Services are provided by payment services providers as Vize Labs may choose from time to time
                    (collectively referred to as the “Payment Processing Services Provider”). You hereby authorize Vize
                    Labs to share with the Payment Processing Services Provider that financial information, banking
                    details and transaction information related to your use of the payment processing services and the
                    Services. You also agree that Vize Labs is not responsible for the use or misuse of such financial
                    information, banking details and transaction information by the Payment Processing Services Provider
                    or any third party.
                </p>
                <p className="service-text">
                    <strong>5.4</strong> <u>Invoices.</u> Payment against any and all invoices rendered by Vize Labs to
                    Customer will be due within 30 calendar days thereof. Late payments shall be subject to a late
                    payment charge of one per cent (1%) per month (12% per annum).
                </p>
                <p className="service-text">
                    <strong>5.5</strong> <u>Taxes.</u> Amounts due under this Agreement are payable to Vize Labs without
                    deduction and are net of any tax, tariff, duty, or assessment imposed by any government authority
                    (national, provincial, or local), including without limitation any sales, use, excise, ad valorem,
                    property, withholding, or value added tax withheld at the source. If applicable law requires
                    withholding or deduction of such taxes or duties, Customer shall separately pay Vize Labs the
                    withheld or deducted amount.
                </p>
            </section>

            <section>
                <h2 className="section-title">6 INTELLECTUAL PROPERTY & FEEDBACK</h2>
                <p className="service-text">
                    <strong>6.1</strong> <u>IP Rights in the Services.</u> Customer acknowledges and agrees that:
                    <ol type="a">
                        <li>Customer does not and will not have or acquire under or in connection with this Agreement
                            any ownership interest in the Services or Documentation, or in any related IP Rights;
                        </li>
                        <li>Vize Labs is and will remain the sole and exclusive owner of all right, title and interest
                            in and to the Services and Documentation, including all IP Rights relating thereto; and
                        </li>
                        <li> Customer hereby unconditionally and irrevocably assigns to Vize Labs (or Vize Labs’
                            designee), its entire right, title and interest in and to any IP Rights that Customer may
                            now or hereafter have in or relating to the Services or Documentation (including any rights
                            in derivative works or patent improvements relating to either of them), whether held or
                            acquired by operation of law, contract, assignment or otherwise.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>6.2</strong> <u>Customer Co-operation and Notice of Infringement.</u> Customer shall, during
                    the Term:
                    <ol type="a">
                        <li>take all reasonable measures to safeguard the Services and Documentation (including all
                            copies thereof) from infringement, misappropriation, theft, misuse or unauthorized access;
                        </li>
                        <li>at Vize Labs’ expense, take all such steps as Vize Labs may reasonably require to assist
                            Vize Labs in maintaining the validity, enforceability and Vize Labs’ ownership of the IP
                            Rights in the Services and Documentation;
                        </li>
                        <li> promptly notify Vize Labs in writing if Customer becomes aware of (I) any actual or
                            suspected infringement, misappropriation or other violation of Vize Labs’ IP Rights in or
                            relating to the Services or Documentation, or (II) any claim that the Services or
                            Documentation, including any production, use, marketing, sale or other disposition of the
                            Services or Documentation, in whole or in part, infringes, misappropriates or otherwise
                            violates the IP Rights or other rights of any person; and
                        </li>
                        <li>fully co-operate with and assist Vize Labs in all reasonable ways in the conduct of any
                            claim, action, proceeding or suit (each, an “Action”) by Vize Labs to prevent or abate any
                            actual or threatened infringement, misappropriation or violation of Vize Labs’ rights in,
                            and to attempt to resolve any claims relating to, the Services or Documentation, including
                            having Customer or Customer’s employees testify when requested and making available for
                            discovery or trial relevant records, papers, information, samples, specimens and the like.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>6.3</strong> <u>No Implied Rights.</u> Except for the limited rights and licenses expressly
                    granted under this Agreement, nothing in this Agreement grants, by implication, waiver, estoppel or
                    otherwise, to Customer or any third party any IP Rights or other right, title or interest in or to
                    any of the Services or Documentation.
                </p>
                <p className="service-text">
                    <strong>6.4</strong> <u>Feedback.</u> Nothing in this Agreement or in the parties’ dealings arising
                    out of or related to this Agreement will restrict Vize Labs’ right to use, profit from, disclose,
                    publish, keep secret, or otherwise exploit Feedback (as defined below), without compensating or
                    crediting Customer or Authorized User in question, except to the limited extent that Article 7
                    (Confidential Information) governs Feedback that constitutes Customer’s Confidential Information.
                    Notwithstanding the provisions of Article 6, Customer may not designate Feedback as its Confidential
                    Information to the extent that such Feedback relates to Vize Labs or its products or services.
                    (“Feedback” refers to any suggestion or idea for improving or otherwise modifying any of Vize Labs’
                    products or services.)
                </p>
                <p className="service-text">
                    <strong>6.5</strong> <u>Use of Customer Data.</u> Subject to these Terms, Vize Labs will access and
                    use Customer Data solely to provide and maintain the Service in accordance with the Agreement (“Use
                    of Customer Data”). Use of Customer Data includes sharing Customer Data as Customer directs through
                    the Service, but Provider will not otherwise disclose Customer Data to third parties except as
                    permitted in this Agreement.
                </p>
                <p className="service-text">
                    <strong>6.6</strong> <u>Use of Customer Data.</u> . Vize Labs may collect Usage Data and use it to
                    operate, improve and support the Service and for other lawful business purposes, including
                    benchmarking and reports. However, Vize Labs will not disclose Usage Data externally unless it is
                    <ol type="a">
                        <li>deidentified so that it does not identify Customer, its Authorized Users or any other person
                            and
                        </li>
                        <li>aggregated with data across other customers.</li>
                    </ol>
                </p>
            </section>

            <section>
                <h2 className="section-title">7 CONFIDENTIAL INFORMATION.</h2>
                <p className="service-text">
                    <strong>7.1</strong> <u>Confidential Information Defined.</u> “Confidential Information” refers to
                    the following documents which one party to this Agreement (“Discloser”) discloses to the other
                    (“Recipient”)
                    <ol type="a">
                        <li>C any document Discloser marks “Confidential”;
                        </li>
                        <li>any information Discloser orally designates as “Confidential” at the time of disclosure,
                            provided Discloser confirms such designation in writing within 3 Business Days;
                        </li>
                        <li> any source code disclosed by Vize Labs, whether or not marked as confidential;
                        </li>
                        <li>any technology, trade secrets, know-how, business operations, plans, strategies, customers,
                            and pricing and information; and
                        </li>
                        <li>
                            any other nonpublic, sensitive information Recipient should reasonably consider a trade
                            secret or otherwise confidential.
                            <p>Notwithstanding the foregoing, Confidential Information does not include information
                                that:</p>
                        </li>
                        <li>is in Recipient’s possession at the time of disclosure;</li>
                        <li>is independently developed by Recipient without use of or reference to Confidential
                            Information;
                        </li>
                        <li>becomes known publicly, before or after disclosure, other than as a result of Recipient’s
                            improper action or inaction; or
                        </li>
                        <li>is approved for release in writing by Discloser.</li>
                    </ol>
                    <p>Recipient is on notice that Confidential Information may include Discloser’s valuable trade
                        secrets.</p>
                </p>
                <p className="service-text">
                    <strong>7.2</strong> <u>Nondisclosure.</u> Recipient shall not:
                    <ol type="a">
                        <li>
                            use Confidential Information for any purpose other than to facilitate the transactions
                            contemplated by this Agreement (the “Purpose”);
                        </li>
                        <li>
                            disclose Confidential Information to any employee or contractor of Recipient unless such
                            person needs access in order to facilitate the Purpose and executes a nondisclosure
                            agreement with Recipient with terms no less restrictive than those of this Article 7; or
                        </li>
                        <li>
                            disclose Confidential Information to any other third party without Discloser’s prior
                            written consent.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>7.3</strong> <u>Protection of Confidential Information.</u> As a condition to being provided
                    with any disclosure of or access to Confidential Information, the Receiving Party shall:
                    <ol type="a">
                        <li>
                            not access or use Confidential Information other than as necessary to exercise its rights or
                            perform its obligations under and in accordance with this Agreement;
                        </li>
                        <li>except as may be permitted under the terms and conditions of Section 7.4, not disclose or
                            permit access to Confidential Information other than to its Representatives who:
                            <ol type="i">
                                <li>need to know such Confidential Information for purposes of the Receiving Party’s
                                    exercise of its rights or performance of its obligations under and in accordance
                                    with this Agreement;
                                </li>
                                <li>have been informed of the confidential nature of the Confidential Information and
                                    the Receiving Party’s obligations under this Article 7; and
                                </li>
                                <li>are bound by confidentiality and restricted use obligations at least as protective
                                    of the Confidential Information as the terms set forth in this Article 7;
                                </li>
                            </ol>
                        </li>
                        <li>
                            safeguard the Confidential Information from unauthorized use, access or disclosure using at
                            least the degree of care it uses to protect its similarly sensitive information and, in no
                            event, less than a reasonable degree of care;
                        </li>
                        <li>
                            promptly notify the Disclosing Party of any unauthorized use or disclosure of Confidential
                            Information and cooperate with Disclosing Party to prevent further unauthorized use or
                            disclosure; and
                        </li>
                        <li>
                            ensure its Representatives’ compliance with, and be responsible and liable for any of its
                            Representatives’ non-compliance with, the terms of this Article 7.
                        </li>
                    </ol>
                    <p>Notwithstanding any other provisions of this Agreement, the Receiving Party’s obligations under
                        this Article 7 with respect to any Confidential Information that constitutes a trade secret
                        under any applicable Law will continue until such time, if ever, as such Confidential
                        Information ceases to qualify for trade secret protection under one or more such applicable Laws
                        other than as a result of any act or omission of the Receiving Party or any of its
                        Representatives.</p>
                </p>
                <p className="service-text">
                    <strong>7.4</strong> <u>Compelled Disclosure.</u> If the Receiving Party or any of its
                    Representatives is compelled by applicable Law to disclose any Confidential Information, then, to
                    the extent permitted by applicable Law, the Receiving Party shall:
                    <ol type="a">
                        <li>
                            promptly, and before such disclosure, notify the Disclosing Party in writing of such
                            requirement so that the Disclosing Party can seek an injunction, protective order or other
                            remedy or waive its rights under Section 7.3; and
                        </li>
                        <li>
                            provide reasonable assistance to the Disclosing Party in opposing such disclosure or seeking
                            an injunction, protective order or other limitations on disclosure.
                        </li>
                    </ol>
                    <p>If the Disclosing Party waives compliance or, after providing the notice and assistance required
                        under this Section 7.4, the Receiving Party remains required by Law to disclose any Confidential
                        Information, the Receiving Party shall disclose only that portion of the Confidential
                        Information that, on the advice of the Receiving Party’s legal counsel, the Receiving Party is
                        legally required to disclose and, on the Disclosing Party’s request, shall use commercially
                        reasonable efforts to obtain assurances from the applicable court or other competent authority
                        that such Confidential Information will be afforded confidential treatment.</p>
                </p>
                <p className="service-text">
                    <strong>7.5</strong> <u>Injunction.</u> Recipient agrees that breach of this Article 7 would cause
                    Discloser irreparable injury, for which monetary damages would not provide adequate compensation,
                    and that in addition to any other remedy, Discloser shall be entitled to injunctive relief against
                    such breach or threatened breach, without proving actual damage or posting a bond or other security.
                </p>
                <p className="service-text">
                    <strong>7.6</strong> <u>Termination & Return.</u> Upon termination of this Agreement, Recipient
                    shall return all copies of Confidential Information to Discloser or certify, in writing, the
                    destruction thereof.
                </p>
                <p className="service-text">
                    <strong>7.7</strong> <u>Retention of Rights.</u> This Agreement does not transfer ownership of
                    Confidential Information or grant a license thereto. Discloser will retain all right, title, and
                    interest in and to all Confidential Information.
                </p>
            </section>

            <section>
                <h2 className="section-title">8 REPRESENTATIONS & WARRANTIES.</h2>
                <p className="service-text">
                    <strong>8.1</strong> <u>Mutual Representations and Warranties.</u> Each party represents, warrants
                    and covenants to the other party that it has validly entered into the Agreement and has the legal
                    power to do so. When executed and delivered by the parties, this Agreement will constitute the
                    legal, valid and binding obligation of such party, enforceable against such party in accordance with
                    its terms, but subject to bankruptcy and other laws affecting creditor’s rights as well as
                    principles of equity.
                </p>
                <p className="service-text">
                    <strong>8.2</strong> <u>Customer Warranty.</u> Customer represents and warrants to Vize Labs that
                    any material provided by Customer to Vize Labs under or in connection with this Agreement, and its
                    use in accordance with this Agreement, will not infringe any rights (including IP Rights) of any
                    third party.
                </p>
                <p className="service-text">
                    <strong>8.3</strong> <u>DISCLAIMER OF WARRANTIES.</u>
                    <ol type="a">
                        <li>ALL SERVICES, DOCUMENTATION AND OTHER PRODUCTS, INFORMATION, MATERIALS AND SERVICES,
                            INCLUDING LIGHTHOUSE SERVICES PROVIDED BY VIZE LABS ARE PROVIDED “AS IS.”
                        </li>
                        <li>VIZE LABS HEREBY DISCLAIMS ALL CONDITIONS AND WARRANTIES, WHETHER EXPRESS, IMPLIED,
                            STATUTORY OR OTHER (INCLUDING ALL CONDITIONS AND WARRANTIES ARISING FROM COURSE OF DEALING,
                            USAGE OR TRADE PRACTICE), AND SPECIFICALLY DISCLAIMS ALL IMPLIED CONDITIONS AND WARRANTIES
                            OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT.
                        </li>
                        <li>WITHOUT LIMITING THE FOREGOING, VIZE LABS MAKES NO CONDITION OR WARRANTY OF ANY KIND THAT
                            THE SERVICES OR DOCUMENTATION, OR ANY OTHER VIZE LABS OR THIRD-PARTY GOODS, SERVICES,
                            TECHNOLOGIES OR MATERIALS (INCLUDING ANY SOFTWARE OR HARDWARE), OR ANY PRODUCTS OR RESULTS
                            OF THE USE OF ANY OF THEM, WILL MEET CUSTOMER’S OR OTHER PERSONS’ REQUIREMENTS, OPERATE
                            WITHOUT INTERRUPTION, ACHIEVE ANY INTENDED RESULT, BE COMPATIBLE OR WORK WITH ANY OTHER
                            GOODS, SERVICES, TECHNOLOGIES OR MATERIALS (INCLUDING ANY SOFTWARE, HARDWARE, SYSTEM OR
                            NETWORK), OR BE SECURE, ACCURATE, COMPLETE, FREE OF HARMFUL CODE OR ERROR FREE.
                        </li>
                        <li> ALL OPEN-SOURCE COMPONENTS AND OTHER THIRD-PARTY MATERIALS ARE PROVIDED “AS IS” AND ANY
                            CONDITION, REPRESENTATION OR WARRANTY OF OR CONCERNING ANY OF THEM IS STRICTLY BETWEEN
                            CUSTOMER AND THE THIRD-PARTY OWNER OR DISTRIBUTOR OF SUCH OPEN-SOURCE COMPONENTS AND
                            THIRD-PARTY MATERIALS.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>8.4</strong> <u>Customer Warranty.</u> Customer agrees that its purchases are not contingent
                    on the delivery of any future functionality or features, or dependent on any oral or written public
                    comments made by Vize Labs regarding future functionality or features.
                </p>
            </section>

            <section>
                <h2 className="section-title">9 INDEMNIFICATION.</h2>
                <p className="service-text">
                    <strong>9.1</strong> <u>From Vize Labs.</u> Vize Labs shall indemnify, defend and hold harmless
                    Customer from and against any and all Losses incurred by Customer arising out of or relating to any
                    Action by a third party to the extent that such Losses arise from any allegation in such Action that
                    the Services, or any use of the Services, in the Territory in accordance with this Agreement
                    (including the Documentation) infringes any Canadian IP Right in the Territory. The foregoing
                    obligation does not apply to the extent that such Action or Losses arise from any allegation of or
                    relating to any:
                    <ol type="a">
                        <li>Open-Source Components or other Third-Party Materials;</li>
                        <li>patent issued on a patent application published after the Effective Date;</li>
                        <li>incorporation by the Services of, or combination, operation or use of the Services in or
                            with, any technology (including any software, hardware, firmware, system or network) or
                            service not provided by Vize Labs or specified for Customer’s use in the Documentation;
                        </li>
                        <li>modification of the Services other than: (I) by Vize Labs in connection with this Agreement;
                            or (II) with Vize Labs’ express written authorization and in strict accordance with Vize
                            Labs’ written directions and specifications;
                        </li>
                        <li>failure to timely implement any Maintenance release, modification, update or replacement of
                            the Services made available to Customer by Vize Labs;
                        </li>
                        <li>use of the Services after Vize Labs’ notice to Customer of such activity’s alleged or actual
                            infringement, misappropriation or other violation of a third party’s rights;
                        </li>
                        <li>negligence, abuse, misapplication or misuse of the Services or Documentation by or on behalf
                            of Customer, Customer’s Representatives or a third party;
                        </li>
                        <li>use of the Services or Documentation by or on behalf of Customer that is outside the
                            purpose, scope or manner of use authorized by this Agreement or in any manner contrary to
                            Vize Labs’ instructions;
                        </li>
                        <li>events or circumstances outside of Vize Labs’ commercially reasonable control (including any
                            third-party hardware, software or system bugs, defects or malfunctions); or
                        </li>
                        <li>Action or Losses for which Customer is obligated to indemnify Vize Labs under Section 9.2.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>9.2</strong> <u>From Customer.</u> Customer shall indemnify, defend and hold harmless Vize
                    Labs from and against any and all Losses incurred by Vize Labs in connection with any Action by a
                    third party to the extent that such Losses arise out of or relate to any allegation:
                    <ol type="a">
                        <li>that any IP Right or other right of any Person, or any Law, is or will be infringed,
                            misappropriated or otherwise violated by any (I) use or combination of the Services by or on
                            behalf of Customer or any of its Representatives with any hardware, software, system,
                            network, service or other matter whatsoever that is neither provided by Vize Labs nor
                            authorized by Vize Labs in this Agreement and the Documentation, and information, materials
                            or technology or other matter whatsoever directly or indirectly provided by Customer or
                            directed by Customer to be installed, combined, integrated or used with, as part of, or in
                            connection with the Services or Documentation;
                        </li>
                        <li>of or relating to facts that, if true, would constitute a breach by Customer of any
                            representation, warranty, covenant or obligation under this Agreement;
                        </li>
                        <li>of or relating to negligence, abuse, misapplication, misuse, injury, death, loss of or
                            damage to real or tangible personal property, or more culpable act or omission (including
                            recklessness or wilful misconduct) by or on behalf of Customer or any of its Representatives
                            with respect to the Services or Documentation or otherwise in connection with this
                            Agreement; or
                        </li>
                        <li>of or relating to use of the Services or Documentation by or on behalf of Customer or any of
                            its Representatives that is outside the purpose, scope or manner of use authorized by this
                            Agreement or the Documentation, or in any manner contrary to Vize Labs’ instructions.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>9.3</strong> <u>Indemnification Procedure.</u> Each party shall promptly notify the other
                    party in writing of any Action for which such party believes it is entitled to be indemnified under
                    Section 9.1 or Section 9.2, as the case may be. The party seeking indemnification (the “Indemnitee”)
                    shall cooperate with the other party (the “Indemnitor”) at the Indemnitor’s sole cost and expense.
                    The Indemnitor shall immediately take control of the defence and investigation of such Indemnified
                    Claim and shall employ counsel of its choice to handle and defend the same, at the Indemnitor’s sole
                    cost and expense. The Indemnitee’s failure to perform any obligations under this Section 9.3 will
                    not relieve the Indemnitor of its obligations under this Section 9.3, except to the extent that the
                    Indemnitor can demonstrate that it has been prejudiced as a result of such failure. The Indemnitee
                    may participate in and observe the proceedings at its own cost and expense with counsel of its own
                    choosing.
                </p>
                <p className="service-text">
                    <strong>9.4</strong> <u>Mitigation.</u> If the Services, or any part of the Services, is, or in Vize
                    Labs’ opinion is likely to be, claimed to infringe, misappropriate or otherwise violate any
                    third-party IP Right, or if Customer’s use of the Services is enjoined or threatened to be enjoined,
                    Vize Labs may, at its option and sole cost and expense:
                    <ol type="a">
                        <li>obtain the right for Customer to continue to use the Services materially as contemplated by
                            this Agreement;
                        </li>
                        <li>modify or replace the Services, in whole or in part, to seek to make the Services
                            non-infringing, while providing materially equivalent features and functionality, and such
                            modified or replacement software will constitute Services under this Agreement; or
                        </li>
                        <li>if none of the remedies set forth in Section 9.4(a) or 9.4(b) is reasonably available to
                            Vize Labs, terminate this Agreement, in its entirety or with respect to the affected part or
                            feature of the Services, effective immediately on written notice to Customer, in which event
                            Customer shall cease all use of the Services and Documentation immediately on receipt of
                            such notice.
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>9.5</strong> <u>SOLE REMEDY.</u> THIS ARTICLE 9 SETS FORTH CUSTOMER’S SOLE REMEDIES AND VIZE
                    LABS’ SOLE LIABILITY AND OBLIGATION FOR ANY ACTUAL, THREATENED OR ALLEGED CLAIMS THAT THIS AGREEMENT
                    OR ANY SUBJECT MATTER HEREOF (INCLUDING THE SERVICES AND DOCUMENTATION) INFRINGES, MISAPPROPRIATES
                    OR OTHERWISE VIOLATES ANY IP RIGHTS OF ANY THIRD PARTY.
                </p>
            </section>

            <section>
                <h2 className="section-title">10 LIMITATION OF LIABILITY.</h2>
                <p className="service-text">
                    <strong>10.1</strong> <u>EXCLUSION OF DAMAGES.</u> IN NO EVENT WILL VIZE LABS, OR ANY OF ITS
                    OFFICERS, DIRECTORS, SHAREHOLDERS, EMPLOYEES, AGENTS, DISTRIBUTORS, RE-SELLERS, CONTRACTORS, SERVICE
                    PROVIDERS OR SUPPLIERS BE LIABLE UNDER OR IN CONNECTION WITH THIS AGREEMENT OR ITS SUBJECT MATTER
                    UNDER ANY LEGAL OR EQUITABLE THEORY, INCLUDING BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE),
                    STRICT LIABILITY AND OTHERWISE, FOR ANY:
                    <ol type="a">
                        <li>INCREASED COSTS, DIMINUTION IN VALUE OR LOST BUSINESS, PRODUCTION, REVENUES OR PROFITS;</li>
                        <li>LOSS OF GOODWILL OR REPUTATION;</li>
                        <li>USE, INABILITY TO USE, LOSS, INTERRUPTION, DELAY OR RECOVERY OF ANY SOFTWARE OR OPEN-SOURCE
                            COMPONENTS OR OTHER THIRD-PARTY MATERIALS;
                        </li>
                        <li>LOSS, DAMAGE, CORRUPTION OR RECOVERY OF DATA, OR BREACH OF DATA OR SYSTEM SECURITY;</li>
                        <li>COST OF REPLACEMENT GOODS OR SERVICES;</li>
                        <li>CONSEQUENTIAL, INCIDENTAL, INDIRECT, SPECIAL, AGGRAVATED, PUNITIVE OR EXEMPLARY DAMAGES;
                            OR
                        </li>
                        <li>DAMAGES ARISING FROM, OR IN RESPECT OF, ANY USE OF THE SERVICES TO SUPPORT THE REPRIMAND,
                            DISMISSAL, TERMINATION, OR DISCIPLINE OF ANY PERSON.
                        </li>
                    </ol>
                    <p>IN EACH CASE REGARDLESS OF WHETHER SUCH PERSONS WERE ADVISED OF THE POSSIBILITY OF SUCH LOSSES OR
                        DAMAGES OR SUCH LOSSES OR DAMAGES WERE OTHERWISE FORESEEABLE, AND NOTWITHSTANDING THE FAILURE OF
                        ANY AGREED OR OTHER REMEDY OF ITS ESSENTIAL PURPOSE.</p>
                </p>
                <p className="service-text">
                    <strong>10.2</strong> <u>CAP ON MONETARY LIABILITY.</u> IN NO EVENT WILL THE AGGREGATE LIABILITY OF
                    VIZE LABS AND ITS OFFICERS, DIRECTORS, SHAREHOLDERS, EMPLOYEES, AGENTS, DISTRIBUTORS, RE-SELLERS,
                    CONTRACTORS, SERVICE PROVIDERS OR SUPPLIERS ARISING OUT OF OR RELATED TO THIS AGREEMENT, WHETHER
                    ARISING UNDER OR RELATED TO BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY OR ANY
                    OTHER LEGAL OR EQUITABLE THEORY, EXCEED THE TOTAL AMOUNTS PAID TO VIZE LABS UNDER THIS AGREEMENT IN
                    THE 12-MONTH PERIOD PRECEDING THE EVENT GIVING RISE TO THE CLAIM. THE FOREGOING LIMITATIONS APPLY
                    EVEN IF ANY REMEDY FAILS OF ITS ESSENTIAL PURPOSE.
                </p>
            </section>

            <section>
                <h2 className="section-title">11 TERM & TERMINATION.</h2>
                <p className="service-text">
                    <strong>11.1</strong> <u>Term.</u> This Agreement commences as of the effective date of the Order
                    Form and will continue in effect as outlined in the Agreement unless terminated earlier under any of
                    the Agreement’s express provisions (the “Initial Term”).
                </p>
                <p className="service-text">
                    <strong>11.2</strong> <u>Renewal Term.</u> This Agreement will automatically renew for successive
                    12-month terms unless earlier terminated under any of the Agreement’s express provisions or either
                    Party gives the other Party written notice of non-renewal at least 60 days before the expiration of
                    the then-current subscription term (each, a “Renewal Term” and , collectively, together with the
                    Initial Term, the “Term”).
                </p>
                <p className="service-text">
                    <strong>11.3</strong> <u>Termination.</u> This Agreement may be terminated at any time:
                    <ol type="a">
                        <li>by Vize Labs, effective on written notice to Customer, if Customer fails to pay any amount
                            when due under this Agreement, where such failure continues for more than 10 days after Vize
                            Labs’ delivery of written notice thereof (the “Payment Failure”);
                        </li>
                        <li>by either party for cause on 30 days written notice to the other party, if the other party
                            materially breaches this Agreement and such breach
                            <ol type="i">
                                <li>is incapable of cure; or</li>
                                <li>being capable of cure, remains uncured at the expiration of such period;</li>
                            </ol>
                        </li>
                        <li>
                            by Vize Labs, effective immediately, if Customer
                            <ol type="i">
                                <li>is dissolved, liquidated or wound-up or takes any corporate action for such
                                    purpose;
                                </li>
                                <li>becomes insolvent or is generally unable to pay its debts as they become due;</li>
                                <li>becomes the subject of any voluntary or involuntary bankruptcy proceeding under any
                                    federal or foreign bankruptcy or insolvency Law;
                                </li>
                                <li>makes or seeks to make a general assignment for the benefit of its creditors; or
                                </li>
                                <li>applies for, or consents to, the appointment of a trustee, receiver,
                                    receiver-manager or custodian for all or a substantial part of its property.
                                </li>
                            </ol>
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>11.4</strong> <u>Effects of Termination.</u> Upon termination of this Agreement or Term,
                    Customer shall cease all use of the Services and delete, destroy, or return all copies of the
                    Services, Documentation, and Vize Labs Confidential Information in its possession or control,
                    including all documents, files and tangible materials (and any partial and complete copies)
                    containing, reflecting, incorporating or based on any of the foregoing, whether or not modified or
                    merged into other materials. The following will survive termination or expiration of this Agreement:
                    <ol type="a">
                        <li>any obligation of Customer to pay Fees incurred before termination;</li>
                        <li>Section 3.4 (Customer Restrictions), Section 3.6 (Non-Vize Labs Providers), Section 3.7
                            (Customer Compliance with Laws and Regulatory Requirements), Article 6 (Intellectual
                            Property & Feedback), Article 7 (Confidential Information), Article 8 (Representations and
                            Warranties), Article 9 (Indemnification), Article 10 (Limitation of Liability), and this
                            Section 11.4; and
                        </li>
                        <li>any other provision of this Agreement that must survive to fulfill its essential purpose.
                        </li>
                    </ol>
                </p>
            </section>

            <section>
                <h2 className="section-title">12 CUSTOMER DATA AND PERSONAL DATA.</h2>
                <p className="service-text">
                    <strong>12.1</strong> <u>Use of Customer Data and Service Attributes.</u> Vize Labs will only use
                    Customer Data and Service Attributes to provide the Services to Customer, except with the prior
                    written consent of Customer or as otherwise permitted under the Agreement. Customer has sole
                    responsibility for the legality, reliability, integrity, accuracy and quality of Customer Data and
                    of the means by which Customer acquires Customer Data. Customer represents and warrants to Vize Labs
                    that Customer has all rights, consents, permissions and legal authority as may be necessary to
                    provide Customer Data to Vize Labs and to authorize Vize Labs to process the Customer Data to
                    provide the Services.
                </p>
                <p className="service-text">
                    <strong>12.2</strong> <u>Disclosure of Customer Data and Service Attributes.</u> Vize Labs will not
                    disclose Customer Data or Service Attributes outside of Vize Labs or its affiliates except:
                    <ol type="a">
                        <li>as Customer directs or as required to provide the Services or services;</li>
                        <li>to Vize Labs subcontractors and service providers to the extent reasonably necessary for the
                            provision of the Services, including to third party intermediaries who assist in the
                            provision of services and the Services;
                        </li>
                        <li>as otherwise described in the Agreement or the Documentation; or</li>
                        <li>as required by Applicable Laws.</li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>12.3</strong> <u>Disclosure of Customer Data and Service Attributes to Governmental
                    Authority.</u> If a Governmental Authority contacts Vize Labs with a demand for Customer Data or
                    Service Attributes, Vize Labs will attempt to redirect the Governmental Authority to request that
                    data directly from Customer. If compelled to disclose Customer Data or Service Attributes to a
                    Governmental Authority, Vize Labs will promptly notify Customer and provide a copy of the demand
                    unless legally prohibited from doing so. Upon receipt of any other third-party request for Customer
                    Data or Service Attributes, Vize Labs will promptly notify Customer unless prohibited by Applicable
                    Laws. Vize Labs will reject the request unless required by Applicable Law to comply. If the request
                    is valid, Vize Labs will attempt to redirect the third party to request the Customer Data or Service
                    Attributes directly from Customer.
                </p>
                <p className="service-text">
                    <strong>12.4</strong> <u>Personal Data.</u> To the extent that the Customer Data includes Personal
                    Data, then:
                    <ol type="a">
                        <li>Customer is the controller of such Personal Data and Vize Labs is solely a processor acting
                            on behalf of Customer; and
                        </li>
                        <li>Vize Labs will promptly report to Customer any requests received from individuals for access
                            to, correction or deletion of, or otherwise related to, their Personal Data.
                        </li>
                    </ol>
                    <p>Subject to the above, Customer consents to Vize Labs’ collection, use and disclosure of Personal
                        Data as provided in the Vize Labs Privacy Policy.</p>
                </p>
                <p className="service-text">
                    <strong>12.5</strong> <u>Data Deletion.</u> This policy outlines the requirements and
                    controls/procedures Vize Labs has implemented to manage the deletion of customer data.
                    <ol type="a">
                        <li>
                            Customer data is retained for as long as the account is in active status. Data enters an
                            “expired” state when the account is voluntarily closed. Expired account data will be
                            retained for 60 days unless the Customer has requested their data be deleted before the 60
                            day retention period. If they request their data to be deleted before the 60 day retention
                            period Vize Labs will remove it within 7 business days following that request. After the
                            retention period, the account and related data will be removed. Customers that wish to
                            voluntarily close their account should download their data manually or via the API prior to
                            closing their account.
                        </li>
                        <li>
                            If a customer account is involuntarily suspended, then there is a 30-day grace period during
                            which the account will be inaccessible but can be reopened if the customer meets their
                            payment obligations and resolves any terms of service violations.
                        </li>
                        <li>
                            If a customer wishes to manually back up their data in a suspended account, then they must
                            ensure that their account is brought back to good standing so that the user interface will
                            be available for their use. After 60 days, the suspended account will be closed, and the
                            data will enter the “expired” state. It will be permanently removed 60 days thereafter
                            (except when required by law to retain).
                        </li>
                        <li>
                            Expired account data will be held for the duration specified in the customer agreement or
                            for a minimum of 60 days, whichever is longer, before permanent deletion, unless otherwise
                            required by applicable law.
                        </li>
                    </ol>
                    <p>This policy is effective immediately and applies to all Vize Labs customers and related data
                        handling processes.</p>
                </p>
            </section>

            <section>
                <h2 className="section-title">13 MISCELLANEOUS.</h2>
                <p className="service-text">
                    <strong>13.1</strong> <u>Headings.</u> The headings in this Agreement are for reference only and
                    shall not affect the interpretation of this Agreement.
                </p>
                <p className="service-text">
                    <strong>13.2</strong> <u>Independent Contractors.</u> The parties are independent contractors and
                    shall so represent themselves in all regards. Neither party is the agent of the other, and neither
                    may make commitments on the other’s behalf. The parties agree that no Vize Labs employee or
                    contractor will be an employee of Customer.
                </p>
                <p className="service-text">
                    <strong>13.3</strong> <u>Authorized Users.</u> Customer is responsible and liable for all uses of
                    the Services and Documentation resulting from access provided by Customer, directly or indirectly,
                    whether such access or use is permitted by or in violation of this Agreement. Without limiting the
                    generality of the foregoing, Customer is responsible and liable for the acts and omissions of
                    Authorized Users related to this Agreement and to the products and services provided pursuant to
                    this Agreement, as if they were Customer’s own acts and omissions. Customer shall take reasonable
                    efforts to make all Authorized Users aware of this Agreement’s provisions as applicable to such
                    Authorized User’s use of the Services and shall cause Authorized Users to comply with such
                    provisions.
                </p>
                <p className="service-text">
                    <strong>13.4</strong> <u>Notices.</u>
                    <ol type="a">
                        <li>Except as otherwise expressly set forth in this Agreement, any notice, request, consent,
                            claim, demand, waiver or other communication under this Agreement will have legal effect
                            only where it is sent to an address or email specified in an Order Form (or to such other
                            address or such other person that such addressee party may designate from time to time).
                        </li>
                        <li>
                            Notices sent in accordance with this Section 13.4 will be deemed effectively given:
                            <ol type="i">
                                <li>when received, if delivered by hand, with signed confirmation of receipt;</li>
                                <li>when received, if sent by a nationally recognized overnight courier, signature
                                    required;
                                </li>
                                <li>when sent, if by facsimile or email, (in each case, with confirmation of
                                    transmission), if sent during the addressee’s normal business hours, and on the next
                                    business day, if sent after the addressee’s normal business hours; and
                                </li>
                                <li>on the 2ND day after the date mailed by certified or registered mail, return receipt
                                    requested, postage prepaid.
                                </li>
                            </ol>
                        </li>
                    </ol>
                </p>
                <p className="service-text">
                    <strong>13.5</strong> <u>Force Majeure.</u> No delay, failure, or default, other than a failure to
                    pay fees when due, will constitute a breach of this Agreement to the extent caused by acts of war,
                    terrorism, hurricanes, earthquakes, other acts of God or of nature, strikes or other labor disputes,
                    riots or other acts of civil disorder, embargoes, failure of communications networks, denial of
                    service/access attacks or other causes beyond the performing party’s reasonable control.
                </p>
                <p className="service-text">
                    <strong>13.6</strong> <u>Assignment.</u> Customer shall not assign or otherwise transfer any of its
                    rights, or delegate or otherwise transfer any of its obligations or performance, under this
                    Agreement without Vize Labs’ prior written consent. Any purported assignment, delegation or transfer
                    in violation of this Section 13.6 is void. This Agreement is binding on and enures to the benefit of
                    the parties and their respective permitted successors and assigns.
                </p>
                <p className="service-text">
                    <strong>13.7</strong> <u>Severability.</u> To the extent permitted by applicable law, the parties
                    hereby waive any provision of law that would render any clause of this Agreement invalid or
                    otherwise unenforceable in any respect. In the event that a provision of this Agreement is held to
                    be invalid or otherwise unenforceable, such provision will be interpreted to fulfill its intended
                    purpose to the maximum extent permitted by applicable law, and the remaining provisions of this
                    Agreement will continue in full force and effect.
                </p>
                <p className="service-text">
                    <strong>13.8</strong> <u>No Waiver.</u> Neither party will be deemed to have waived any of its
                    rights under this Agreement by lapse of time or by any statement or representation other than by an
                    authorized representative in an explicit written waiver. No waiver of a breach of this Agreement
                    will constitute a waiver of any other breach of this Agreement.
                </p>
                <p className="service-text">
                    <strong>13.9</strong> <u>Governing Law and Forum:</u> This Agreement is governed by and construed in
                    accordance with the Laws of the Province of Saskatchewan and the federal Laws of Canada applicable
                    therein. Any Action arising out of or related to this Agreement or the licences granted hereunder
                    will be instituted exclusively in the courts of the Province of Saskatchewan, and each party
                    irrevocably submits to the exclusive jurisdiction of such courts in any such Action. Service of
                    process notice or other document by mail to such party’s address set forth herein will be effective
                    service of process for any Action brought in any such court. The parties hereby expressly exclude
                    the application of the United Nations Convention on Contracts for the International Sale of Goods.
                </p>
                <p className="service-text">
                    <strong>13.10</strong> <u>Construction.</u> The parties agree that the terms of this Agreement
                    result from negotiations between them. This Agreement will not be construed in favor of or against
                    either party by reason of authorship.
                </p>
                <p className="service-text">
                    <strong>13.11</strong> <u>Export.</u> Customer acknowledges that the Services will not be exported
                    except as authorized by the laws of the jurisdiction in which the Services was obtained.
                </p>
                <p className="service-text">
                    <strong>13.12</strong> <u>Entire Agreement.</u> This Agreement sets forth the entire agreement of
                    the parties and supersedes all prior or contemporaneous writings, negotiations, and discussions with
                    respect to its subject matter. Neither party has relied upon any such prior or contemporaneous
                    communications.
                </p>
                <p className="service-text">
                    <strong>13.13</strong> <u>Counterparts; Electronic Transmission.</u> Any Order Form or Statement of
                    Work may be executed in counterparts and such counterparts together shall constitute a single
                    instrument. Delivery of an executed counterpart of an Order Form or Statement of Work by electronic
                    means, including, without limitation, by facsimile transmission or by electronic transmission of
                    portable document format (“.pdf”) file or other electronic file, shall be equally effective as
                    delivery of a manually executed counterpart. The parties hereto acknowledge and agree that in any
                    legal proceedings between them respecting or in any way relating to this Agreement, each waives the
                    right to raise any defence based on the execution in counterparts or the delivery of executed
                    counterparts by electronic means.
                </p>
                <p className="service-text">
                    <strong>13.14</strong> <u>Electronic Signatures and Electronic Delivery.</u> Each party agrees that
                    the electronic signatures, whether digital or encrypted, of the parties included in this Agreement
                    are intended to authenticate this writing and to have the same force and effect as manual
                    signatures. Electronic signature means any electronic sound, symbol, or process attached to or
                    logically associated with a record and executed and adopted by a party with the intent to sign such
                    record, including facsimile or email electronic signatures. Delivery of an executed copy of this
                    Agreement by facsimile or electronic transmission constitutes valid and effective delivery
                </p>
                <p className="service-text">
                    <strong>13.15</strong> <u>Amendment.</u> This Agreement may not be amended except through a written
                    agreement by authorized representatives of each party.
                </p>
                <p className="service-text">
                    <strong>13.16</strong> <u>Dispute Resolution.</u> Should a dispute arise regarding any invoice or
                    any other matter in connection with this Agreement, the parties agree that they shall meet and
                    negotiate in good faith to attempt to resolve the dispute. If the dispute cannot be resolved though
                    good faith negotiations between the parties within 5 days then either party may provide the other
                    with written notice of the need to appoint a mediator. Should the parties fail to agree upon a
                    mediator within 5 days after the notice or the parties fail to resolve the dispute by way of
                    mediation then such dispute shall be finally resolved by binding arbitration pursuant to the
                    provisions of The Arbitration Act, 1992 (Saskatchewan) (the “Act”), as from time to time in force or
                    as amended. The arbitration shall be administrated by the Rules of the Court of Queen’s Bench and
                    the Rules of the Court of Appeal for Saskatchewan, in accordance with section 58 of the Act. The
                    arbitration shall take place in Saskatoon, Saskatchewan before a single arbitrator selected by
                    agreement of the parties. Should the parties fail to agree on an arbitrator, the arbitrator shall be
                    appointed by a court of competent jurisdiction pursuant to the Act. The costs of arbitration,
                    including legal fees and disbursements, shall be awarded by the arbitrator in the manner which the
                    arbitrator considers appropriate under the circumstances.
                    <p>THE PARTIES WAIVE ANY RIGHT TO ASSERT ANY CLAIMS AGAINST THE OTHER PARTY AS A REPRESENTATIVE OR
                        MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION, EXCEPT WHERE SUCH WAIVER IS PROHIBITED BY LAW OR
                        DEEMED BY A COURT OF LAW TO BE AGAINST PUBLIC POLICY. TO THE EXTENT EITHER PARTY IS PERMITTED BY
                        LAW OR COURT OF LAW TO PROCEED WITH A CLASS OR REPRESENTATIVE ACTION AGAINST THE OTHER, THE
                        PARTIES AGREE THAT: (I) THE PREVAILING PARTY SHALL NOT BE ENTITLED TO RECOVER ATTORNEYS’ FEES OR
                        COSTS ASSOCIATED WITH PURSUING THE CLASS OR REPRESENTATIVE ACTION (NOT WITHSTANDING ANY OTHER
                        PROVISION IN THIS AGREEMENT); AND (II) THE PARTY WHO INITIATES OR PARTICIPATES AS A MEMBER OF
                        THE CLASS WILL NOT SUBMIT A CLAIM OR OTHERWISE PARTICIPATE IN ANY RECOVERY SECURED THROUGH THE
                        CLASS OR REPRESENTATIVE ACTION.</p>
                </p>
            </section>
        </div>
    );
};

export default TermsOfService;

